/**
 * EmailVerificationModeration — Admin panel listing users whose email is
 * either NOT verified yet (scope='unverified') or has been auto-blocked
 * because the 90-day grace period elapsed (scope='blocked').
 *
 * Actions: send/resend verification code via EmailJS; delete the account.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, RefreshCw, Mail, AlertTriangle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const EmailVerificationModeration = ({ scope }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/admin/email-verifications?scope=${scope}`);
      setUsers(r.data?.users || []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const sendCode = async (userId) => {
    setSendingId(userId);
    try {
      const r = await axios.post(`/api/admin/email-verifications/${userId}/send`);
      if (r.data?.sent) {
        toast.success('Code envoyé par email');
      } else if (r.data?.already_verified) {
        toast.info('Email déjà vérifié');
      } else {
        toast.warning('Code généré, envoi automatique en échec. Vérifiez la config EmailJS.');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSendingId(null);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer définitivement le compte ${email} ?`)) return;
    try {
      await axios.delete(`/api/admin/email-verifications/${userId}`);
      toast.success('Compte supprimé');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const runBlockTask = async () => {
    try {
      const r = await axios.post('/api/admin/email-verifications/run-block-task');
      toast.success(`${r.data?.newly_blocked || 0} compte(s) bloqué(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="bg-white/5 rounded-2xl p-6 border border-white/10" data-testid={`email-mod-${scope}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm text-white/70">
          {scope === 'blocked' ? (
            <>
              <ShieldAlert className="w-4 h-4 text-red-400" />
              <span>Comptes bloqués pour email non vérifié dans les 90 jours</span>
            </>
          ) : (
            <>
              <Mail className="w-4 h-4 text-amber-300" />
              <span>Utilisateurs dont l'email n'est pas encore vérifié</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runBlockTask}
            className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-400/30 flex items-center gap-1.5"
            title="Bloquer manuellement les comptes >90 j non vérifiés"
            data-testid="email-mod-run-block"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Tâche blocage
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white flex items-center gap-1.5"
            data-testid={`email-mod-${scope}-refresh`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Recharger
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-white/40 text-sm">Chargement…</div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm" data-testid={`email-mod-${scope}-empty`}>
          {scope === 'blocked' ? 'Aucun compte bloqué pour le moment' : 'Tous les utilisateurs ont vérifié leur email'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`email-mod-${scope}-table`}>
            <thead>
              <tr className="text-left text-white/50 uppercase text-[10px] tracking-wider border-b border-white/10">
                <th className="py-2 px-2">Utilisateur</th>
                <th className="py-2 px-2">Email</th>
                <th className="py-2 px-2">Inscrit depuis</th>
                <th className="py-2 px-2">Code</th>
                <th className="py-2 px-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const v = u.verification || {};
                const sent = v.sent_at;
                const expires = v.expires_at;
                return (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`email-mod-row-${u.id}`}>
                    <td className="py-2 px-2">
                      <div className="font-semibold text-white">{u.name}</div>
                      <div className="text-[11px] text-white/40">@{u.username}</div>
                    </td>
                    <td className="py-2 px-2 text-white/80">{u.email}</td>
                    <td className="py-2 px-2 text-white/70">
                      {u.age_days != null ? `${u.age_days} j` : '—'}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-white/60">
                      {sent ? (
                        <>
                          <div>✉️ envoyé</div>
                          {expires && <div>exp. {new Date(expires).toLocaleDateString()}</div>}
                        </>
                      ) : v.code ? (
                        <span className="text-amber-300">généré (pas envoyé)</span>
                      ) : (
                        <span className="text-white/40">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => sendCode(u.id)}
                          disabled={sendingId === u.id}
                          className="px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50"
                          data-testid={`email-mod-send-${u.id}`}
                        >
                          <Send className="w-3 h-3" />
                          {sent ? 'Renvoyer' : 'Envoyer'}
                        </button>
                        <button
                          onClick={() => deleteUser(u.id, u.email)}
                          className="px-2 py-1 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300 text-[11px] font-semibold flex items-center gap-1"
                          data-testid={`email-mod-delete-${u.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailVerificationModeration;
